import { AnyAction } from 'redux';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { AssessmentPagePayload } from 'types/assessment-payload';
import { getAbortController, getAssessmentPage } from 'api/api';
import {
  checkSelectionAction,
  fetchAssessmentPageErrorAction,
  fetchAssessmentPageStartAction,
  fetchAssessmentPageSuccessAction,
} from 'actions/assessmentPage';
import { prepareUpdatePageAssessmentMessages } from 'actions/messages';
import { CheckMessage } from '../../types/check';
import { Review } from '../../types/review-category';
import { GET_ASSESSMENT_PAGE } from 'actions/assessmentPage/types';
import { RootReducer } from 'store/reducers';
import { getAlertByExternalId } from 'store/reducers/assessmentPage/selectors';
import {
  accuracyReportToPageAssessments,
  getImportedReport,
} from 'pqaAccuracy/dataTransfer/reportImportCache';

const getState = (state: RootReducer) => state as RootReducer;

const labelMessagesWithPageNumber = (
  pageNumber: number,
  messages: CheckMessage[]
): CheckMessage[] => {
  return messages.map((msg: CheckMessage) => {
    msg.pageNumber = pageNumber;
    return msg;
  });
};

export function* fetchAssessmentPage({ pageNumber, token }: AnyAction) {
  try {
    const preState = (yield select(getState)) as RootReducer;

    const importedReport = getImportedReport();
    let importedPages = !!importedReport
      ? accuracyReportToPageAssessments(importedReport)
      : null;

    const prepareUpdate = (messages: CheckMessage[]) =>
      put(
        prepareUpdatePageAssessmentMessages(
          pageNumber,
          labelMessagesWithPageNumber(pageNumber, messages) as CheckMessage[]
        )
      );

    if (!!importedPages) {
      importedPages.forEach((p, i) => {
        preState.assessmentPage.pages[i + 1] =
          preState.assessmentPage.pages[i + 1] || p;
      });
    }

    const cachedPage =
      preState.assessmentPage.pages[pageNumber + 1] ??
      (importedPages && importedPages[pageNumber]);
    // we use indexing starting from 1

    if (!!cachedPage) {
      yield put(fetchAssessmentPageStartAction());
      getAbortController().abort();
      const currentPage = cachedPage;
      yield put(fetchAssessmentPageSuccessAction(undefined, cachedPage));

      yield prepareUpdate(currentPage[Review.branding].messages);
      yield prepareUpdate(currentPage[Review.claims].messages);
      yield prepareUpdate(currentPage[Review.safety].messages);
      yield prepareUpdate(currentPage[Review.style].messages);
      yield prepareUpdate(currentPage.messages ?? []);

      const state = yield select(getState);
      const externalAlert = yield getAlertByExternalId(state);
      if (externalAlert) yield put(checkSelectionAction(externalAlert.id));

      return;
    }

    yield put(fetchAssessmentPageStartAction());
    getAbortController().abort();
    const { data: currentPage }: { data: AssessmentPagePayload } = yield call(
      getAssessmentPage,
      pageNumber,
      token
    );
    yield put(fetchAssessmentPageSuccessAction(currentPage));

    yield prepareUpdate(currentPage[Review.branding].messages);
    yield prepareUpdate(currentPage[Review.claims].messages);
    yield prepareUpdate(currentPage[Review.safety].messages);
    yield prepareUpdate(currentPage[Review.style].messages);
    yield prepareUpdate(currentPage.messages ?? []);

    const state = yield select(getState);
    const externalAlert = yield getAlertByExternalId(state);

    if (externalAlert) {
      yield put(checkSelectionAction(externalAlert.id));
    }
  } catch (e) {
    console.error(e);
    if (e.response) {
      yield put(
        fetchAssessmentPageErrorAction(
          e.response?.data?.message,
          e.response?.status
        )
      );
    }
  }
}

export default function* watchFetchAssessmentPage() {
  yield takeEvery(GET_ASSESSMENT_PAGE, fetchAssessmentPage);
}
